import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDevice, useUpdateDevice, useDeleteDevice } from "../../api/devices";
import { useDeviceModels, useDeviceTypes } from "../../api/deviceTypes";
import { deviceModelLabel } from "../../api/deviceModelLabel";
import { StatusBadge } from "../../components/StatusBadge";
import { DeviceEventLog } from "../../components/DeviceEventLog";
import { extractErrorMessage } from "../../api/errors";
import { useGroups } from "../../api/groups";
import { ArrowLeft, Copy, Info, Smartphone, Hash, Cpu, Calendar, Clock, Pencil, Trash2, X, Settings2 } from "lucide-react";
import { buildDeviceUpdateRequest, resolveDeviceTypeIdForModel } from "./deviceDetailHelpers";

function CopyButton({ text }: { text: string }) {
  return (
    <button
      onClick={() => navigator.clipboard.writeText(text)}
      className="text-gray-400 hover:text-riot-gold-500 dark:hover:text-riot-gold-400"
      aria-label="Copy"
    >
      <Copy size={12} />
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0 dark:border-riot-navy-700">
      <dt className="text-xs text-gray-500 dark:text-riot-navy-400">{label}</dt>
      <dd className="text-xs text-gray-900 dark:text-riot-navy-100">{children}</dd>
    </div>
  );
}

function SectionCard({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
        {icon}
        {title}
        <Info size={13} className="ml-auto text-gray-300 dark:text-riot-navy-500" />
      </h3>
      <dl>{children}</dl>
    </div>
  );
}

export function DeviceDetailPage() {
  const { imei } = useParams<{ imei: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useDevice(imei ?? "");
  const groupsQuery = useGroups({ counts: false });
  const deviceTypesQuery = useDeviceTypes();
  const updateDevice = useUpdateDevice();
  const deleteDevice = useDeleteDevice();

  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");
  const [editDeviceTypeId, setEditDeviceTypeId] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editGroup, setEditGroup] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [formError, setFormError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const allModelsQuery = useDeviceModels();
  const modelsQuery = useDeviceModels({ deviceTypeId: editDeviceTypeId || undefined, enabled: !!editDeviceTypeId });
  const groups = useMemo(
    () => [...(groupsQuery.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [groupsQuery.data?.data],
  );
  const deviceTypes = useMemo(
    () => [...(deviceTypesQuery.data?.data ?? [])].filter((type) => type.status !== "deprecated").sort((a, b) => a.name.localeCompare(b.name)),
    [deviceTypesQuery.data?.data],
  );
  const deviceModels = useMemo(
    () => [...(modelsQuery.data?.data ?? [])].filter((entry) => entry.status !== "deprecated").sort((a, b) => a.name.localeCompare(b.name)),
    [modelsQuery.data?.data],
  );
  const allDeviceModels = useMemo(
    () => [...(allModelsQuery.data?.data ?? [])].filter((entry) => entry.status !== "deprecated"),
    [allModelsQuery.data?.data],
  );
  const hasVisibleModelOption = useMemo(
    () => !!editModel && deviceModels.some((entry) => entry.modelCode === editModel),
    [deviceModels, editModel],
  );

  // When the query resolves, seed the form with the current values so
  // toggling edit-mode doesn't start with empty strings that look like
  // the user wants to blank the fields.
  useEffect(() => {
    if (data?.data && !editMode) {
      setEditName(data.data.deviceName ?? "");
      setEditContactNumber(data.data.contactNumber ?? "");
      setEditDeviceTypeId(data.data.deviceTypeId ?? "");
      setEditModel(data.data.model ?? "");
      setEditGroup(data.data.groupId ?? "");
      setEditDescription(data.data.description ?? "");
    }
  }, [data?.data, editMode]);

  // When navigated here with { state: { edit: true } } — e.g. the "Edit" button
  // on the Devices list — open straight into edit mode once the device loads.
  // One-shot (a ref, not a re-runnable condition) so Cancel actually closes the
  // editor instead of it immediately re-opening.
  const autoEditApplied = useRef(false);
  useEffect(() => {
    const wantsEdit = (location.state as { edit?: boolean } | null)?.edit;
    if (wantsEdit && data?.data && !autoEditApplied.current) {
      autoEditApplied.current = true;
      setEditMode(true);
    }
  }, [data?.data, location.state]);

  useEffect(() => {
    if (!editMode || editDeviceTypeId || !editModel) return;
    const resolved = resolveDeviceTypeIdForModel(editModel, allDeviceModels, deviceTypes);
    if (!resolved.deviceTypeId) return;
    setEditDeviceTypeId(resolved.deviceTypeId);
    if (resolved.modelIsInCatalog && resolved.modelCode !== editModel) {
      setEditModel(resolved.modelCode);
    }
  }, [allDeviceModels, deviceTypes, editDeviceTypeId, editMode, editModel]);

  if (isLoading) return <p className="text-sm text-gray-400 dark:text-riot-navy-400">Loading device...</p>;
  if (error) return <p className="text-sm text-red-600 dark:text-red-400">Error: {extractErrorMessage(error, "Failed to load device")}</p>;
  if (!data) return null;

  const d = data.data;

  async function saveEdit() {
    setFormError("");
    const trimmedName = editName.trim();
    try {
      await updateDevice.mutateAsync({
        imei: d.imei,
        body: buildDeviceUpdateRequest({
          device: d,
          // IMEI is read-only for tenants — submit it unchanged.
          imei: d.imei,
          name: trimmedName,
          contactNumber: editContactNumber,
          groupId: editGroup,
          deviceTypeId: editDeviceTypeId,
          model: editModel,
          description: editDescription,
          deviceModels: allDeviceModels,
        }),
      });
      setEditMode(false);
    } catch (err) {
      setFormError(extractErrorMessage(err, "Failed to save device"));
    }
  }

  async function performDelete() {
    setDeleteError("");
    try {
      await deleteDevice.mutateAsync(d.imei);
      navigate("/devices");
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Failed to delete device"));
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate("/devices")}
        className="mb-4 flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        <ArrowLeft size={14} /> Back to devices
      </button>

      {/* Header card */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-riot-navy-50 text-riot-navy-500 dark:bg-riot-navy-700 dark:text-riot-navy-300">
              <Smartphone size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  IMEI: <span className="font-mono">{d.imei}</span>
                </span>
                <CopyButton text={d.imei} />
              </div>
              <p className="mt-0.5 text-xs text-gray-400 dark:text-riot-navy-400">
                {d.deviceName} &middot; Model: {deviceModelLabel(d.model, allDeviceModels) || "Unknown"}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <StatusBadge status={d.status} variant="dot" />
            {!editMode && (
              <>
                <button
                  onClick={() => navigate(`/devices/${encodeURIComponent(d.imei)}/shadow-settings`)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                >
                  <Settings2 size={12} /> Shadow settings
                </button>
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/30"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {editMode && (
        <div className="mb-4 rounded-lg border border-riot-gold-200 bg-riot-gold-50/40 p-5 dark:border-riot-gold-900/40 dark:bg-riot-navy-800">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Edit device</h3>
            <button
              onClick={() => { setEditMode(false); setFormError(""); }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-navy-200"
              aria-label="Close editor"
            >
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">IMEI</span>
              {/* IMEI is the device's hardware identity — read-only here. Changing
                  it (or the owning company) is a platform-admin action, not a
                  tenant one. */}
              <div
                aria-label="IMEI"
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600 dark:border-riot-navy-700 dark:bg-riot-navy-900/50 dark:text-riot-navy-300"
              >
                {d.imei}
              </div>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Name</span>
              <input
                aria-label="Device name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Contact number <span className="font-normal text-gray-400 dark:text-riot-navy-400">(optional)</span>
              </span>
              <input
                aria-label="Contact number"
                type="tel"
                value={editContactNumber}
                onChange={(e) => setEditContactNumber(e.target.value)}
                placeholder="+27 82 000 0000"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Group</span>
              <select
                aria-label="Device group"
                value={editGroup}
                onChange={(e) => setEditGroup(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
              >
                <option value="">No group</option>
                {groups.map((group) => (
                  <option key={group.groupId} value={group.groupId}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Type</span>
              <select
                aria-label="Device type"
                value={editDeviceTypeId}
                onChange={(e) => {
                  setEditDeviceTypeId(e.target.value);
                  setEditModel("");
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
              >
                <option value="">No type</option>
                {deviceTypes.map((type) => (
                  <option key={type.deviceTypeId} value={type.deviceTypeId}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Model</span>
              <select
                aria-label="Device model"
                value={editModel}
                onChange={(e) => setEditModel(e.target.value)}
                disabled={!editDeviceTypeId}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
              >
                <option value="">No model</option>
                {editModel && !hasVisibleModelOption && (
                  <option value={editModel}>{editModel} (current)</option>
                )}
                {deviceModels.map((entry) => (
                  <option key={entry.modelCode} value={entry.modelCode}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-3 flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700 dark:text-riot-navy-200">Description</span>
            <textarea
              aria-label="Device description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={2}
              placeholder="Optional notes"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
            />
          </label>
          {formError && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">{formError}</p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              onClick={saveEdit}
              disabled={updateDevice.isPending}
              className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
            >
              {updateDevice.isPending ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={() => { setEditMode(false); setFormError(""); }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-5 dark:border-red-900/50 dark:bg-red-950/20">
          <h3 className="mb-2 text-sm font-semibold text-red-800 dark:text-red-300">Delete this device?</h3>
          <p className="mb-3 text-xs text-red-700 dark:text-red-300/80">
            Soft-deletes the device record. It stops appearing in lists immediately and is permanently removed after 30 days. Active tasks targeting it will skip the device rather than fail.
          </p>
          {deleteError && (
            <p className="mb-3 text-xs text-red-700 dark:text-red-300">{deleteError}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={performDelete}
              disabled={deleteDevice.isPending}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
            >
              {deleteDevice.isPending ? "Deleting…" : "Confirm delete"}
            </button>
            <button
              onClick={() => { setConfirmDelete(false); setDeleteError(""); }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tabs (placeholder for future Tasks/Changes tabs) */}
      <div className="mb-4 flex border-b border-gray-200 dark:border-riot-navy-700">
        <button className="border-b-2 border-riot-gold-500 px-4 py-2 text-xs font-medium text-riot-gold-600 dark:text-riot-gold-400">
          Device details
        </button>
        <button className="border-b-2 border-transparent px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-riot-navy-300 dark:hover:text-white">
          Tasks
        </button>
        <button className="border-b-2 border-transparent px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-riot-navy-300 dark:hover:text-white">
          Changes
        </button>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SectionCard title="General" icon={<Cpu size={14} className="text-riot-gold-500" />}>
          <Field label="IMEI">
            <span className="font-mono">{d.imei}</span>
          </Field>
          <Field label="Name">{d.deviceName}</Field>
          <Field label="Contact">{d.contactNumber || <span className="text-gray-400 dark:text-riot-navy-400">-</span>}</Field>
          <Field label="Model">{deviceModelLabel(d.model, allDeviceModels) || <span className="text-gray-400 dark:text-riot-navy-400">-</span>}</Field>
          <Field label="Status"><StatusBadge status={d.status} variant="dot" /></Field>
        </SectionCard>

        <SectionCard title="Firmware" icon={<Hash size={14} className="text-riot-gold-500" />}>
          <Field label="Version">{d.firmwareVersion || <span className="text-gray-400 dark:text-riot-navy-400">-</span>}</Field>
          <Field label="Hardware">{d.hardwareVersion || <span className="text-gray-400 dark:text-riot-navy-400">-</span>}</Field>
          <Field label="Group ID">{d.groupId || <span className="text-gray-400 dark:text-riot-navy-400">Unassigned</span>}</Field>
        </SectionCard>

        <SectionCard title="Timestamps" icon={<Clock size={14} className="text-riot-gold-500" />}>
          <Field label="Last seen">
            {d.lastSeen ? new Date(d.lastSeen).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">Never</span>}
          </Field>
          <Field label="Registered">
            <span className="flex items-center gap-1">
              <Calendar size={11} className="text-gray-400" />
              {new Date(d.createdAt).toLocaleDateString()}
            </span>
          </Field>
          <Field label="Updated">{new Date(d.updatedAt).toLocaleDateString()}</Field>
        </SectionCard>
      </div>

      <DeviceEventLog imei={d.imei} />
    </div>
  );
}
